import type { FC, ReactNode } from 'react'

import Image from 'next/image'
import { useDictionary } from '@/lib/i18n'
import Reveal from '@/components/landing/reveal'
import SectionHeading from '@/components/landing/section-heading'

import { BIKE_IMAGES } from '@/constants/bike'

const EngineeringSection: FC = (): ReactNode => {
    const t = useDictionary()
    const { engineering } = t
    const image = BIKE_IMAGES.frontQuarter

    return (
        <section id='engineering' aria-labelledby='engineering-title' className='relative overflow-hidden border-t border-border py-24 sm:py-32'>
            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2 lg:gap-16'>
                <Reveal className='relative'>
                    <div aria-hidden='true' className='pointer-events-none absolute inset-[12%] rounded-full bg-race/25 blur-[100px]' />

                    <Image
                        src={image.src}
                        width={image.width}
                        height={image.height}
                        alt={engineering.imageAlt}
                        sizes='(min-width: 1280px) 600px, (min-width: 1024px) 50vw, 100vw'
                        className='relative h-auto w-full'
                    />

                    <div className='absolute bottom-0 left-0 rounded-2xl border border-white/10 bg-black/65 px-5 py-4 backdrop-blur'>
                        <p className='font-heading text-5xl font-bold leading-none tracking-tight text-foreground'>
                            {engineering.weight.value}
                            <span className='ml-1 text-2xl text-race-bright'>
                                {engineering.weight.unit}
                            </span>
                        </p>
                        <p className='mt-2 font-mono text-[11px] uppercase tracking-[0.16em] text-muted-foreground'>
                            {engineering.weight.label}
                        </p>
                    </div>
                </Reveal>

                <div>
                    <Reveal>
                        <SectionHeading id='engineering-title' eyebrow={engineering.eyebrow} title={engineering.title} />
                    </Reveal>

                    <Reveal delay={80}>
                        <p className='mt-6 text-base leading-relaxed text-muted-foreground text-pretty sm:text-lg'>
                            {engineering.body}
                        </p>
                    </Reveal>

                    <ul className='mt-10 grid gap-x-8 gap-y-8 sm:grid-cols-2'>
                        {engineering.points.map((point, index) => (
                            <li key={point.title}>
                                <Reveal delay={index * 80}>
                                    <div className='border-t border-border pt-5'>
                                        <h3 className='display-heading font-heading text-xl font-semibold uppercase tracking-tight text-foreground'>
                                            {point.title}
                                        </h3>
                                        <p className='mt-2 text-sm leading-relaxed text-muted-foreground'>
                                            {point.body}
                                        </p>
                                    </div>
                                </Reveal>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </section>
    )
}

export default EngineeringSection
